<template>
  <AppLayout>
    <Teleport to="body">
      <div v-if="scaleDetectBusy || segmentBusy" class="global-loading-mask">
        <div class="global-loading-inner">
          <div class="spinner"></div>
          <div class="loading-text">{{ loadingOverlayText }}</div>
        </div>
      </div>
    </Teleport>
    <div class="page">
      <SemStepper active-key="step1" />

      <div class="content">
        <aside class="left">
          <div class="left-head">
            <div class="left-title">SEM图片导入</div>
            <button class="ghost" type="button" @click="backToLanding">
              重新导入
            </button>
          </div>

          <div class="history-block">
            <div class="history-title">历史记录</div>
            <div v-if="historyLoading" class="history-empty">加载中…</div>
            <div v-else-if="!historyTasks.length" class="history-empty">暂无历史记录</div>
            <button
              v-for="h in historyTasks"
              :key="h.task_id"
              type="button"
              class="history-item"
              :class="{ active: String(h.task_id) === String(taskId) }"
              @click="openHistoryTask(h.task_id)"
            >
              <div class="history-item-stack">
                <div class="history-list-thumb-wrap" aria-hidden="true">
                  <img
                    v-if="historyListThumbUrl(h)"
                    class="history-list-thumb"
                    :src="historyListThumbUrl(h)"
                    alt=""
                    referrerpolicy="no-referrer"
                  />
                  <span v-else class="history-list-thumb-ph">—</span>
                </div>
                <span class="history-item-id" :title="h.task_id">{{ h.task_id }}</span>
              </div>
            </button>
          </div>

          <div
            v-if="taskMeta && (taskMeta.materialStructure || taskMeta.materialComposition)"
            class="meta-block"
          >
            <div class="meta-row"><span class="mk">材料结构</span><span class="mv">{{ taskMeta.materialStructure || '—' }}</span></div>
            <div class="meta-row"><span class="mk">材料组成</span><span class="mv">{{ taskMeta.materialComposition || '—' }}</span></div>
            <div v-if="taskMeta.importMode === 'order' && taskMeta.orderId" class="meta-row">
              <span class="mk">订单</span><span class="mv">{{ taskMeta.orderLabel || taskMeta.orderId }}</span>
            </div>
          </div>
        </aside>

        <section class="center">
          <div class="topbar">
            <div class="topbar-left">
              <div class="topbar-title">标尺长度</div>
              <input v-model="scaleValue" class="scale-input" type="number" min="0" />
              <select v-model="scaleUnit" class="scale-select">
                <option value="μm">μm</option>
                <option value="nm">nm</option>
                <option value="mm">mm</option>
              </select>
            </div>
          </div>

          <div class="canvas">
            <div class="img-stage" v-if="previewUrl" ref="stage">
              <img
                class="canvas-img"
                ref="canvasImg"
                :src="previewUrl"
                alt="SEM"
                @load="onImgLoad"
                @error="onImgError"
              />

              <div
                class="roi-layer"
                ref="roiLayer"
                @pointerdown="onPointerDown"
                @pointermove="onPointerMove"
                @pointerup="onPointerUp"
                @pointercancel="onPointerUp"
              >
                <div
                  v-if="roi.visible"
                  class="roi-rect"
                  :style="roiRectStyle"
                  @pointerdown.stop="onRectPointerDown"
                >
                  <div
                    v-for="h in roiHandles"
                    :key="h"
                    class="roi-handle"
                    :class="`h-${h}`"
                    @pointerdown.stop="onHandlePointerDown(h, $event)"
                  ></div>
                </div>
              </div>
            </div>
            <div v-else class="canvas-ph">请先导入 SEM 图片</div>
          </div>

          <div class="footer">
            <button
              class="secondary"
              type="button"
              @click="goNext"
              :disabled="scaleDetectBusy || segmentBusy"
            >
              {{ segmentBusy ? '填色大约需要1-5分钟，请稍后' : '下一步' }}
            </button>
          </div>
        </section>

        <aside class="right">
          <div class="guide">
            <div class="guide-head">标尺识别说明</div>

            <div class="guide-steps">
              <div class="gstep">
                <div class="gstep-no">01</div>
                <div class="gstep-body">
                  <div class="gstep-text">在左侧历史记录中选择任务；在中间画布确认已导入的 SEM 图片。</div>
                </div>
              </div>

              <div class="gstep">
                <div class="gstep-no">02</div>
                <div class="gstep-body">
                  <div class="gstep-text">
                    在中间大图上框选标尺区域（绿色框）。可拖拽控制点调整范围，尽量覆盖标尺与数值区域。
                  </div>
                  <div class="gstep-mini">
                    <div class="mini-ph" v-if="!previewUrl">示意图</div>
                    <img v-else class="mini-img" :src="previewUrl" alt="" />
                  </div>
                </div>
              </div>

              <div class="gstep">
                <div class="gstep-no">03</div>
                <div class="gstep-body">
                  <div class="gstep-text">填写/确认上方标尺长度与单位，然后点击“下一步”。</div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '../../../layouts/AppLayout.vue';
import SemStepper from '../../../components/SemStepper.vue';
import { getSemTask, getSemTaskHistory, runSegmentation } from '../../../services/semApi';
import { promptLogin } from '../../../utils/auth';

const SEM_TASK_STORAGE_KEY = 'sem_current_task_id';

export default {
  name: 'SemStep1Scale',
  components: { AppLayout, SemStepper },
  data() {
    return {
      taskId: this.$route.query.taskId || null,
      previewUrl: null,
      scaleValue: 100,
      scaleUnit: 'μm',
      scaleDetectBusy: false,
      segmentBusy: false,
      autoScaleDone: false,
      // 标尺横线像素长度（用于把像素换算为物理单位）
      scalePxLength: null,

      // ROI 矩形（显示坐标系：相对于图片渲染后的像素坐标）
      roi: {
        visible: false,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
      },
      roiHandles: ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'],
      drag: {
        mode: null, // 'draw' | 'move' | 'resize'
        handle: null,
        pointerId: null,
        startX: 0,
        startY: 0,
        base: null,
      },
      imgMeta: {
        naturalW: 0,
        naturalH: 0,
        renderW: 0,
        renderH: 0,
        offsetX: 0,
        offsetY: 0,
      },
      pendingRoiNat: null,
      taskMeta: null,
      historyTasks: [],
      historyLoading: true,
    };
  },
  computed: {
    loadingOverlayText() {
      if (this.segmentBusy) return '填色大约需要1-5分钟，请稍后';
      if (this.scaleDetectBusy) return '标尺识别中，请稍候…';
      return '';
    },
    roiRectStyle() {
      const x = Math.round(this.roi.x);
      const y = Math.round(this.roi.y);
      const w = Math.round(this.roi.w);
      const h = Math.round(this.roi.h);
      return {
        transform: `translate(${Math.round(this.imgMeta.offsetX + x)}px, ${Math.round(
          this.imgMeta.offsetY + y,
        )}px)`,
        width: `${w}px`,
        height: `${h}px`,
      };
    },
  },
  mounted() {
    this.resolveTaskId();
    this.loadTaskFromDb();
    this.loadTaskHistory();
  },
  methods: {
    /** 第一步仅展示用户原始上传图（库字段 img_url），不使用填色/编辑后的 overlay */
    historyListThumbUrl(h) {
      const u = h?.img_url;
      return u && String(u).trim() ? String(u).trim() : null;
    },
    resolveTaskId() {
      if (this.taskId) {
        sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
        return;
      }
      const cached = sessionStorage.getItem(SEM_TASK_STORAGE_KEY);
      if (cached) {
        this.taskId = cached;
        this.$router.replace({ query: { ...this.$route.query, taskId: cached } });
      }
    },
    async loadTaskHistory() {
      this.historyLoading = true;
      try {
        this.historyTasks = await getSemTaskHistory(50, { segmentStatus: 'all' });
      } catch (e) {
        console.error(e);
      } finally {
        this.historyLoading = false;
      }
    },
    openHistoryTask(taskId) {
      const next = String(taskId || '').trim();
      if (!next || next === this.taskId) return;
      sessionStorage.setItem(SEM_TASK_STORAGE_KEY, next);
      // 必须整页跳到带 taskId 的 URL；router.push 后立即 reload 可能尚未更新地址栏，导致仍加载旧任务
      const to = this.$router.resolve({ name: 'semStep1', query: { taskId: next } });
      window.location.assign(to.href);
    },
    async loadTaskFromDb() {
      if (!this.taskId) return;
      try {
        sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
        const row = await getSemTask(this.taskId);
        this.taskMeta = {
          materialStructure: row?.material_structure || '',
          materialComposition: row?.material_composition || '',
          importMode: 'local',
        };
        // 第一步所有展示（主画布、侧栏小图）均使用用户原始上传图，不使用填色/overlay
        if (row?.img_url) {
          this.previewUrl = row.img_url;
          this.scaleDetectBusy = true;
        }
        if (row?.scale_value !== null && row?.scale_value !== '' && Number.isFinite(Number(row?.scale_value))) {
          this.scaleValue = Number(row.scale_value);
        } else {
          this.scaleValue = 100;
        }
        if (row?.scale_unit) this.scaleUnit = row.scale_unit;
        if (Number.isFinite(Number(row?.scale_px_length))) this.scalePxLength = Number(row.scale_px_length);
        const parseMaybeJson = (v) => {
          if (!v) return null;
          if (typeof v === 'object') return v;
          if (typeof v === 'string') {
            try { return JSON.parse(v); } catch (e) { return null; }
          }
          return null;
        };
        const ruler = parseMaybeJson(row?.ruler_info);
        const roiNat = ruler?.roi || null;
        if (roiNat && Number.isFinite(Number(roiNat.x)) && Number.isFinite(Number(roiNat.y))) {
          this.pendingRoiNat = {
            x: Number(roiNat.x),
            y: Number(roiNat.y),
            w: Number(roiNat.w) || 0,
            h: Number(roiNat.h) || 0,
          };
          // 有历史ROI时，回显优先，不再自动识别覆盖
          this.autoScaleDone = true;
        }
      } catch (e) {
        console.error(e);
      }
    },
    round3(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return null;
      return Math.round(n * 1000) / 1000;
    },
    onImgLoad() {
      const img = this.$refs.canvasImg;
      if (!img) return;
      this.imgMeta.naturalW = img.naturalWidth || 0;
      this.imgMeta.naturalH = img.naturalHeight || 0;
      this.syncRenderSize();

      if (this.pendingRoiNat) {
        this.setRoiFromBackend(this.pendingRoiNat);
        this.pendingRoiNat = null;
        this.scaleDetectBusy = false;
        return;
      }

      // 选择图片后：自动调用标尺识别接口，并回显到绿色 ROI 框
      this.maybeAutoDetectScale();
    },
    onImgError() {
      this.scaleDetectBusy = false;
    },
    async maybeAutoDetectScale() {
      if (this.autoScaleDone) return;
      if (!this.previewUrl) {
        this.scaleDetectBusy = false;
        return;
      }
      if (!this.imgMeta.naturalW || !this.imgMeta.naturalH) return;

      this.autoScaleDone = true;
      try {
        const resp = await fetch(this.previewUrl, { mode: 'cors' });
        if (!resp.ok) throw new Error(`Failed to load image: ${resp.status}`);
        const blob = await resp.blob();

        const fd = new FormData();
        fd.append('image', blob, 'sem.png');
        fd.append('do_ocr', '0');
        if (this.taskId) fd.append('task_id', String(this.taskId));
        fd.append('client_id', String(this.taskId || 'anonymous'));
        if (this.previewUrl) fd.append('img_url', String(this.previewUrl));
        if (this.taskMeta?.materialStructure) fd.append('material_structure', String(this.taskMeta.materialStructure));
        if (this.taskMeta?.materialComposition) fd.append('material_composition', String(this.taskMeta.materialComposition));

        // 通过 Nginx 代理的相对路径访问 Python API
        const r = await fetch('/api/sem/scale', {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (r.status === 401) {
          promptLogin();
          throw new Error('未登录或登录已失效');
        }
        const json = await r.json();
        if (json.code === 401) {
          promptLogin();
          throw new Error(json.msg || '未登录或登录已失效');
        }
        if (json.code !== 200) {
          throw new Error(json.msg || 'scale api failed');
        }

        const data = json.data || {};
        if (data.found && data.bar) {
          this.scalePxLength = this.round3(data.bar.pxLength);

          // 2选B：根据 bar 的端点坐标自行推导一个矩形 ROI（不依赖 bbox 字段）
          const roiNat = this.computeRoiFromBar(data.bar);
          if (roiNat) this.setRoiFromBackend(roiNat);

          // 可选：把 OCR 读数回填到输入框（若返回了）
          if (data.scale && typeof data.scale.value === 'number' && data.scale.unit) {
            this.scaleValue = this.round3(data.scale.value);
            this.scaleUnit = data.scale.unit;
          }
        }
      } catch (err) {
        // 出错不阻断页面使用，让用户继续手动框选
        console.error(err);
      } finally {
        this.scaleDetectBusy = false;
      }
    },
    computeRoiFromBar(bar) {
      // bar: {x1,y1,x2,y2}
      const x1 = Number(bar.x1);
      const y1 = Number(bar.y1);
      const x2 = Number(bar.x2);
      const y2 = Number(bar.y2);
      if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) return null;
      const naturalW = this.imgMeta.naturalW;
      const naturalH = this.imgMeta.naturalH;
      if (!naturalW || !naturalH) return null;

      // 优先使用后端返回的 bbox：它比 x1/y1/y2 更贴合“横线粗细”的真实位置
      const box = bar.bbox || null;
      if (
        box &&
        Number.isFinite(Number(box.x)) &&
        Number.isFinite(Number(box.y)) &&
        Number.isFinite(Number(box.w)) &&
        Number.isFinite(Number(box.h))
      ) {
        const baseLeft = Number(box.x);
        const baseTop = Number(box.y);
        const baseW = Number(box.w);
        const baseH = Number(box.h);

        // 用 bar.y1 与 bbox.y 的相对位置判断文字/标尺块可能在横线的哪一侧：
        // 你的样例里：bar.y1 比 bbox.y 大，横线在 bbox 的下半部分，这通常意味着“文字/标尺块”更可能在 bbox 上方，
        // 因此需要更大的 padTop，而不是简单地 padBottom 继续扩大。
        const lineOffsetFromTop = y1 - baseTop;
        const lineInBottomHalf = lineOffsetFromTop > baseH / 2;

        const padX = Math.max(10, Math.round(baseW * 0.08));
        const padTop = lineInBottomHalf
          ? Math.max(18, Math.round(baseH * 1.3))
          : Math.max(8, Math.round(baseH * 0.9));
        const padBottom = lineInBottomHalf
          ? Math.max(8, Math.round(baseH * 0.6))
          : Math.max(18, Math.round(baseH * 1.1));

        const left = baseLeft - padX;
        const right = baseLeft + baseW + padX;
        const top = baseTop - padTop;
        const bottom = baseTop + baseH + padBottom;

        const roiX = Math.max(0, Math.round(left));
        const roiY = Math.max(0, Math.round(top));
        const roiW = Math.max(
          8,
          Math.min(naturalW - roiX, Math.round(right - roiX)),
        );
        const roiH = Math.max(
          8,
          Math.min(naturalH - roiY, Math.round(bottom - roiY)),
        );

        return { x: roiX, y: roiY, w: roiW, h: roiH };
      }

      // 兜底：用端点推导
      const dx = Math.abs(x2 - x1);
      const padX = Math.max(18, Math.round(dx * 0.22));
      const padTop = 24;
      const padBottom = 14;

      const left = Math.min(x1, x2) - padX;
      const right = Math.max(x1, x2) + padX;
      const top = Math.min(y1, y2) - padTop;
      const bottom = Math.max(y1, y2) + padBottom;

      const roiX = Math.max(0, Math.round(left));
      const roiY = Math.max(0, Math.round(top));
      const roiW = Math.max(8, Math.min(naturalW - roiX, Math.round(right - roiX)));
      const roiH = Math.max(8, Math.min(naturalH - roiY, Math.round(bottom - roiY)));

      return { x: roiX, y: roiY, w: roiW, h: roiH };
    },
    syncRenderSize() {
      const img = this.$refs.canvasImg;
      if (!img) return;

      // 由于使用了 object-fit: contain，图片内容不会铺满整个 <img> 元素：
      // 实际用于映射坐标的“内容区域”需要根据 contain 的缩放比例重新计算 offset。
      const viewW = img.clientWidth || 0;
      const viewH = img.clientHeight || 0;
      if (!viewW || !viewH || !this.imgMeta.naturalW || !this.imgMeta.naturalH) return;

      const scale = Math.min(viewW / this.imgMeta.naturalW, viewH / this.imgMeta.naturalH);
      const contentW = this.imgMeta.naturalW * scale;
      const contentH = this.imgMeta.naturalH * scale;

      // offset 是内容区域相对于 <img> 元素左上角的偏移（letterbox padding）
      this.imgMeta.offsetX = Math.round((viewW - contentW) / 2);
      this.imgMeta.offsetY = Math.round((viewH - contentH) / 2);

      // renderW/renderH：用于坐标换算与边界裁剪的“内容区域”尺寸
      this.imgMeta.renderW = Math.round(contentW);
      this.imgMeta.renderH = Math.round(contentH);

      // 如果 ROI 超出边界，收回到边界内
      this.clampRoiToBounds();
    },
    clampRoiToBounds() {
      const maxW = this.imgMeta.renderW;
      const maxH = this.imgMeta.renderH;
      if (!maxW || !maxH || !this.roi.visible) return;
      let { x, y, w, h } = this.roi;
      w = Math.max(1, Math.min(w, maxW));
      h = Math.max(1, Math.min(h, maxH));
      x = Math.max(0, Math.min(x, maxW - w));
      y = Math.max(0, Math.min(y, maxH - h));
      this.roi = { ...this.roi, x, y, w, h };
    },
    getLocalPoint(evt) {
      const layer = this.$refs.roiLayer;
      if (!layer) return { x: 0, y: 0 };
      const rect = layer.getBoundingClientRect();
      const x = evt.clientX - rect.left;
      const y = evt.clientY - rect.top;
      // 转成“相对图片内容区域”的坐标（因为图片是 contain 居中，会有留白）
      return {
        x: x - this.imgMeta.offsetX,
        y: y - this.imgMeta.offsetY,
      };
    },
    isPointInImage(p) {
      return (
        p.x >= 0 &&
        p.y >= 0 &&
        p.x <= this.imgMeta.renderW &&
        p.y <= this.imgMeta.renderH
      );
    },
    beginDrag(evt, mode, handle = null) {
      const p = this.getLocalPoint(evt);
      this.drag.mode = mode;
      this.drag.handle = handle;
      this.drag.pointerId = evt.pointerId;
      this.drag.startX = p.x;
      this.drag.startY = p.y;
      this.drag.base = { ...this.roi };
      evt.currentTarget.setPointerCapture?.(evt.pointerId);
    },
    onPointerDown(evt) {
      // 在空白处按下：开始画一个新框（只允许一个 ROI，新框会覆盖旧框）
      this.syncRenderSize();
      const p = this.getLocalPoint(evt);
      if (!this.isPointInImage(p)) return;
      this.roi = { visible: true, x: p.x, y: p.y, w: 1, h: 1 };
      this.beginDrag(evt, 'draw');
    },
    onRectPointerDown(evt) {
      // 在已有框里按下：移动
      this.beginDrag(evt, 'move');
    },
    onHandlePointerDown(handle, evt) {
      // 拖动控制点：缩放
      this.beginDrag(evt, 'resize', handle);
    },
    onPointerMove(evt) {
      if (this.drag.pointerId == null || evt.pointerId !== this.drag.pointerId) return;
      const p = this.getLocalPoint(evt);
      const dx = p.x - this.drag.startX;
      const dy = p.y - this.drag.startY;

      const boundsW = this.imgMeta.renderW;
      const boundsH = this.imgMeta.renderH;
      if (!boundsW || !boundsH) return;

      const base = this.drag.base || this.roi;
      let x = base.x;
      let y = base.y;
      let w = base.w;
      let h = base.h;

      if (this.drag.mode === 'draw') {
        const x2 = base.x + dx;
        const y2 = base.y + dy;
        x = Math.min(base.x, x2);
        y = Math.min(base.y, y2);
        w = Math.abs(dx);
        h = Math.abs(dy);
      } else if (this.drag.mode === 'move') {
        x = base.x + dx;
        y = base.y + dy;
      } else if (this.drag.mode === 'resize') {
        const hx = this.drag.handle;
        let right = base.x + base.w;
        let bottom = base.y + base.h;
        let left = base.x;
        let top = base.y;

        if (hx.includes('w')) left = base.x + dx;
        if (hx.includes('e')) right = base.x + base.w + dx;
        if (hx === 'n') top = base.y + dy;
        if (hx === 's') bottom = base.y + base.h + dy;
        if (hx === 'nw') top = base.y + dy;
        if (hx === 'ne') top = base.y + dy;
        if (hx === 'sw') bottom = base.y + base.h + dy;
        if (hx === 'se') bottom = base.y + base.h + dy;

        x = Math.min(left, right);
        y = Math.min(top, bottom);
        w = Math.abs(right - left);
        h = Math.abs(bottom - top);
      }

      // 最小尺寸与边界限制
      w = Math.max(8, w);
      h = Math.max(8, h);
      x = Math.max(0, Math.min(x, boundsW - w));
      y = Math.max(0, Math.min(y, boundsH - h));

      this.roi = { visible: true, x, y, w, h };
    },
    onPointerUp(evt) {
      if (this.drag.pointerId == null || evt.pointerId !== this.drag.pointerId) return;
      this.drag.mode = null;
      this.drag.handle = null;
      this.drag.pointerId = null;
      this.drag.base = null;
      // 如果太小，认为是误触，隐藏
      if (this.roi.visible && (this.roi.w < 10 || this.roi.h < 10)) {
        this.roi.visible = false;
      }
    },
    // 将 ROI 从“显示坐标”换算到“原图坐标”
    getRoiInNaturalCoords() {
      const { naturalW, naturalH, renderW, renderH } = this.imgMeta;
      if (!naturalW || !naturalH || !renderW || !renderH || !this.roi.visible) return null;
      const sx = naturalW / renderW;
      const sy = naturalH / renderH;
      return {
        x: Math.round(this.roi.x * sx),
        y: Math.round(this.roi.y * sy),
        w: Math.round(this.roi.w * sx),
        h: Math.round(this.roi.h * sy),
      };
    },
    // 将后端传入的 ROI（原图坐标系）回显成当前渲染坐标系
    // roiNat: {x,y,w,h}
    setRoiFromBackend(roiNat) {
      this.syncRenderSize();
      const { naturalW, naturalH, renderW, renderH } = this.imgMeta;
      if (!naturalW || !naturalH || !renderW || !renderH || !roiNat) return;
      const sx = renderW / naturalW;
      const sy = renderH / naturalH;
      this.roi = {
        visible: true,
        x: roiNat.x * sx,
        y: roiNat.y * sy,
        w: roiNat.w * sx,
        h: roiNat.h * sy,
      };
      this.clampRoiToBounds();
    },
    backToLanding() {
      this.$router.push({ name: 'semLanding' });
    },
    async goNext() {
      if (!this.taskId) {
        this.$router.push({ name: 'semLanding' });
        return;
      }

      if (this.segmentBusy) return;
      this.segmentBusy = true;
      try {
        // 用户可手动调整 ROI：下一步时优先采用当前 ROI 的原图宽度作为标尺像素长度。
        // 这样 scaleValue(如 100μm) 会和“当前框选的标尺线长度”绑定，而不是沿用旧识别值。
        const roiNat = this.getRoiInNaturalCoords();
        let effectiveScalePxLength = this.scalePxLength;
        if (roiNat && Number.isFinite(Number(roiNat.w)) && Number(roiNat.w) > 0) {
          // 去除框选边框自身宽度（.roi-rect 为 2px 实线边框，左右各一次）
          const natPerRenderX = this.imgMeta.renderW > 0 ? (this.imgMeta.naturalW / this.imgMeta.renderW) : 1;
          const borderNat = Math.max(0, Math.round(2 * natPerRenderX) * 2);
          effectiveScalePxLength = Math.max(1, Number(roiNat.w) - borderNat);
        }
        this.scalePxLength = effectiveScalePxLength;

        let ratio = null;
        if (effectiveScalePxLength && effectiveScalePxLength > 0) {
          ratio = this.scaleValue / effectiveScalePxLength;
        }

        const res = await runSegmentation({
          taskId: this.taskId,
          previewUrl: this.previewUrl,
          ratio,
          unit: this.scaleUnit,
          clientId: this.taskId || 'anonymous',
          materialStructure: this.taskMeta?.materialStructure || '',
          materialComposition: this.taskMeta?.materialComposition || '',
          rulerInfo: {
            value: this.scaleValue,
            unit: this.scaleUnit,
            pxLength: effectiveScalePxLength,
            roi: roiNat || null,
          },
        });

        if (!res.overlayUrl || !res.labelUrl) {
          throw new Error('填色失败：后端未返回 overlay/label 地址');
        }

        this.$router.push({
          name: 'semStep2',
          query: { taskId: this.taskId },
        });
        sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));
      } catch (e) {
        console.error(e);
        alert(e?.message || '填色失败，请重试');
      } finally {
        this.segmentBusy = false;
      }
    },
  },
};
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 0;
}

.content {
  display: grid;
  grid-template-columns: 180px 1fr 440px;
  grid-template-rows: 1fr;
  gap: 10px;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}

.left {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 14px;
  padding: 10px;
  overflow: auto;
}

.left-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.left-title {
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
}

.ghost {
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: rgba(255, 255, 255, 0.8);
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 11px;
  cursor: pointer;
}

.history-block {
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 10px;
  background: rgba(248, 250, 252, 0.9);
  padding: 8px;
}

.history-title {
  font-size: 11px;
  color: #64748b;
  margin-bottom: 6px;
}

.history-empty {
  font-size: 11px;
  color: #94a3b8;
  padding: 6px 2px;
}

.history-item {
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.98);
  padding: 8px;
  margin-bottom: 8px;
  display: block;
  cursor: pointer;
  text-align: center;
}

.history-item-stack {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
}

.history-list-thumb-wrap {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #0b1220;
  display: flex;
  align-items: center;
  justify-content: center;
}

.history-list-thumb {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.history-list-thumb-ph {
  font-size: 12px;
  color: #64748b;
}

.history-item-id {
  display: block;
  font-size: 11px;
  color: #0f172a;
  font-weight: 600;
  line-height: 1.35;
  min-width: 0;
  padding: 0 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

.history-item.active {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background: rgba(239, 246, 255, 0.95);
}

.history-item.active .history-list-thumb-wrap {
  border-color: #2563eb;
}

.meta-block {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(248, 250, 252, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.22);
  font-size: 11px;
  line-height: 1.45;
}

.meta-row {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 6px;
  padding: 3px 0;
}

.meta-row + .meta-row {
  border-top: 1px dashed rgba(148, 163, 184, 0.28);
}

.mk {
  color: #64748b;
}

.mv {
  color: #0f172a;
  word-break: break-all;
}

.center {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  align-items: stretch;
  height: 100%;
}

.topbar {
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 14px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.topbar-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
}

.scale-input,
.scale-select {
  border: 1px solid rgba(148, 163, 184, 0.4);
  border-radius: 10px;
  padding: 7px 10px;
  font-size: 13px;
  outline: none;
  background: #ffffff;
}

.scale-input {
  width: 140px;
}

.scale-select {
  width: 84px;
}

.canvas {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 14px;
  overflow: hidden;
  /* 画布占满中间列，且在固定高度内滚动查看大图（避免撑开页面） */
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
}

.canvas-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain; /* 重要：不裁剪，完整展示 */
}

.global-loading-mask {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.global-loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #e5e7eb;
  font-size: 12px;
}

.spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid rgba(226, 232, 240, 0.5);
  border-top-color: #3b82f6;
  animation: spin 0.8s linear infinite;
  margin-bottom: 8px;
}

.loading-text {
  letter-spacing: 0.5px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.img-stage {
  position: relative;
  width: 100%;
  height: 100%;
  background: #0b1220;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* 当图片以 contain 展示时，两侧/上下可能出现留白，用更柔和的背景提升观感 */
.img-stage::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(900px 520px at 50% 40%, rgba(148, 163, 184, 0.22), rgba(11, 18, 32, 0) 60%),
    linear-gradient(180deg, rgba(15, 23, 42, 0.85), rgba(11, 18, 32, 0.92));
  opacity: 1;
}

.canvas-img {
  position: relative;
  z-index: 1;
}

.roi-layer {
  z-index: 2;
}

.roi-layer {
  position: absolute;
  inset: 0;
  touch-action: none;
  user-select: none;
}

.roi-rect {
  position: absolute;
  left: 0;
  top: 0;
  border: 2px solid #34d399;
  background: rgba(52, 211, 153, 0.22);
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.25) inset;
}

.roi-handle {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: #ecfeff;
  border: 1px solid rgba(16, 185, 129, 0.85);
}

.roi-handle.h-nw { left: -4px; top: -4px; cursor: nwse-resize; }
.roi-handle.h-ne { right: -4px; top: -4px; cursor: nesw-resize; }
.roi-handle.h-sw { left: -4px; bottom: -4px; cursor: nesw-resize; }
.roi-handle.h-se { right: -4px; bottom: -4px; cursor: nwse-resize; }
.roi-handle.h-n { left: 50%; top: -4px; transform: translateX(-50%); cursor: ns-resize; }
.roi-handle.h-s { left: 50%; bottom: -4px; transform: translateX(-50%); cursor: ns-resize; }
.roi-handle.h-w { left: -4px; top: 50%; transform: translateY(-50%); cursor: ew-resize; }
.roi-handle.h-e { right: -4px; top: 50%; transform: translateY(-50%); cursor: ew-resize; }

.canvas-ph {
  color: #64748b;
  font-size: 12px;
}

.footer {
  display: flex;
  justify-content: flex-start;
  margin-top: auto; /* 把“下一步”推到页面底部 */
  padding-top: 4px;
}

.secondary {
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 12px;
  cursor: pointer;
}

.guide {
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 14px;
  padding: 12px;
  height: 100%;
}

.guide-head {
  font-size: 14px;
  font-weight: 900;
  color: #0f172a;
  margin-bottom: 10px;
}

.guide-steps {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-left: 2px;
}

/* 竖向虚线（模仿参考模板的步骤连线） */
.guide-steps::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 6px;
  bottom: 6px;
  border-left: 2px dashed rgba(16, 185, 129, 0.35);
}

.gstep {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 10px;
  align-items: start;
  position: relative;
}

.gstep-no {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 900;
  color: #3b82f6;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.35);
  z-index: 1;
}

.gstep-body {
  min-width: 0;
}

.gstep-text {
  font-size: 13px;
  color: #334155;
  line-height: 1.55;
}

.gstep-mini {
  margin-top: 10px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: #0b1220;
}

.mini-img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
  opacity: 0.92;
}

.mini-ph {
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(226, 232, 240, 0.75);
  font-size: 12px;
}

/* 旧的右侧表单样式已移除（标尺输入已移到中间顶部条） */

@media (max-width: 1100px) {
  .content {
    grid-template-columns: 160px 1fr;
  }

  .right {
    grid-column: 1 / -1;
  }

  .canvas {
    min-height: 420px;
  }
}
</style>

